import React, { useState, useEffect } from 'react';
import { useAction, useQuery } from 'wasp/client/operations';
import { updateTransportation, getTransportationById } from 'wasp/client/operations';
import { useAuth } from 'wasp/client/auth';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { DashboardLayout } from '../../client/components/Sidebar';
import { useLocationContext } from '../../client/contexts/LocationContext';
import { resolveLocationSlug, locationSectionPath } from '../../client/lib/locationPaths';
import {
  IndividualEditPageLayout,
  SidebarSection,
  ActionButtons,
  ActionButton,
  ActionButtonSecondary
} from '../../components/layout';
import {
  Car,
  DollarSign,
  Phone,
  Save,
  AlertCircle,
  HelpCircle,
  Star,
  MapPin,
  Users
} from 'lucide-react';

interface TransportationFormData {
  title: string;
  description: string;
  listingType: 'OFFER' | 'REQUEST';
  routeFrom: string;
  routeTo: string;
  scheduleType: 'DAILY' | 'WEEKLY' | 'ONE_TIME' | 'FLEXIBLE';
  schedule: string;
  seats: string;
  price: string;
  vehicleType: string;
  contactInfo: string;
  visibility: 'PUBLIC' | 'SUBSCRIBER' | 'VERIFIED';
}

const SCHEDULE_TYPES = [
  { value: 'DAILY', label: 'Daily' },
  { value: 'WEEKLY', label: 'Weekly' },
  { value: 'ONE_TIME', label: 'One Time' },
  { value: 'FLEXIBLE', label: 'Flexible' }
];

const VEHICLE_TYPES = ['Sedan', 'SUV', 'Van/Minivan', 'Truck', 'Bus', 'Motorcycle', 'Other'];

const VISIBILITY_OPTIONS = [
  { value: 'VERIFIED', label: 'Verified Members', description: 'Visible to verified members only' },
  { value: 'PENDING', label: 'Pending Review', description: 'Will be reviewed before publishing' }
];

const Tooltip = ({ children, content }: { children: React.ReactNode; content: string }) => {
  const [isVisible, setIsVisible] = useState(false);
  return (
    <div className="relative inline-block">
      <div onMouseEnter={() => setIsVisible(true)} onMouseLeave={() => setIsVisible(false)} className="cursor-help">
        {children}
      </div>
      {isVisible && (
        <div className="absolute z-10 px-3 py-2 text-sm text-white bg-gray-900 rounded-lg shadow-lg bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-64">
          {content}
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
        </div>
      )}
    </div>
  );
};

export default function EditTransportationPage() {
  const { data: user } = useAuth();
  const { selectedLocation } = useLocationContext();
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const { itemId, slug } = useParams<{ itemId: string; slug?: string }>();
  const locSlug = resolveLocationSlug(routerLocation.pathname, slug, selectedLocation);
  const transportationListPath = locationSectionPath(locSlug, 'transportation');

  const updateTransportationFn = useAction(updateTransportation);
  const { data: item, isLoading, error } = useQuery(getTransportationById, { itemId: itemId || '' });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formData, setFormData] = useState<TransportationFormData>({
    title: '',
    description: '',
    listingType: 'OFFER',
    routeFrom: '',
    routeTo: '',
    scheduleType: 'DAILY',
    schedule: '',
    seats: '',
    price: '',
    vehicleType: '',
    contactInfo: '',
    visibility: 'VERIFIED'
  });

  useEffect(() => {
    if (item) {
      const itemData = item as any;
      setFormData({
        title: itemData.title || '',
        description: itemData.description || '',
        listingType: itemData.metadata?.listingType || 'OFFER',
        routeFrom: itemData.metadata?.routeFrom || '',
        routeTo: itemData.metadata?.routeTo || '',
        scheduleType: itemData.metadata?.scheduleType || 'DAILY',
        schedule: itemData.metadata?.schedule || '',
        seats: itemData.metadata?.seats?.toString() || '',
        price: itemData.metadata?.price?.toString() || '',
        vehicleType: itemData.metadata?.vehicleType || '',
        contactInfo: itemData.metadata?.contactInfo || '',
        visibility: itemData.visibility || 'VERIFIED'
      });
    }
  }, [item]);

  const handleChange = (field: keyof TransportationFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.title.trim()) newErrors.title = 'Title is required';
    if (!formData.description.trim()) newErrors.description = 'Description is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    if (!itemId) { setErrors({ submit: 'Item ID is missing' }); return; }

    setIsSubmitting(true);
    try {
      await updateTransportationFn({
        itemId,
        title: formData.title.trim(),
        description: formData.description.trim(),
        listingType: formData.listingType,
        routeFrom: formData.routeFrom.trim(),
        routeTo: formData.routeTo.trim(),
        scheduleType: formData.scheduleType,
        schedule: formData.schedule.trim(),
        seats: formData.seats ? parseInt(formData.seats) : undefined,
        price: formData.price ? parseFloat(formData.price) : undefined,
        vehicleType: formData.vehicleType,
        metadata: { contactInfo: formData.contactInfo.trim() }
      });
      handleGoBack();
    } catch (error) {
      console.error('Error updating transportation item:', error);
      setErrors({ submit: 'Failed to update item. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoBack = () => {
    navigate(transportationListPath);
  };

  if (!user) {
    return (
      <DashboardLayout>
        <div className="p-6 text-center py-12">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <p className="text-gray-600">You must be logged in to edit transportation items.</p>
        </div>
      </DashboardLayout>
    );
  }

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="p-6 text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading item...</p>
        </div>
      </DashboardLayout>
    );
  }

  if (error || !item) {
    return (
      <DashboardLayout>
        <div className="p-6 text-center py-12">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Item Not Found</h3>
          <button onClick={handleGoBack} className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700">Go Back</button>
        </div>
      </DashboardLayout>
    );
  }

  if ((item as any).authorId !== user.id) {
    return (
      <DashboardLayout>
        <div className="p-6 text-center py-12">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Access Denied</h3>
          <p className="text-gray-600 mb-4">You can only edit your own items.</p>
          <button onClick={handleGoBack} className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700">Go Back</button>
        </div>
      </DashboardLayout>
    );
  }

  const sidebarContent = (
    <>
      <SidebarSection isFirst>
        <ActionButtons>
          <ActionButton type="submit" form="transportation-form" disabled={isSubmitting || !formData.title.trim()} icon={Save}>
            {isSubmitting ? 'Updating...' : 'Update'}
          </ActionButton>
          <ActionButtonSecondary onClick={handleGoBack} disabled={isSubmitting}>Cancel</ActionButtonSecondary>
        </ActionButtons>
      </SidebarSection>

      <SidebarSection title="Visibility Settings">
        <div className="space-y-3">
          {VISIBILITY_OPTIONS.map((option) => (
            <label key={option.value} className="flex items-center space-x-3 cursor-pointer">
              <input
                type="radio"
                name="visibility"
                value={option.value}
                checked={formData.visibility === option.value}
                onChange={(e) => handleChange('visibility', e.target.value as 'VERIFIED' | 'PENDING')}
                className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
              />
              <div className="flex items-center gap-2">
                <div className="font-medium text-gray-900">{option.label}</div>
                <Tooltip content={option.description}>
                  <HelpCircle className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                </Tooltip>
              </div>
            </label>
          ))}
        </div>
      </SidebarSection>

      <SidebarSection title="Tips">
        <div className="space-y-3 text-sm text-gray-600">
          <div className="flex items-start gap-2">
            <Star className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
            <span>Be specific about pickup location and timing</span>
          </div>
          <div className="flex items-start gap-2">
            <Star className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
            <span>Clarify cost sharing vs. full charge</span>
          </div>
          <div className="flex items-start gap-2">
            <Star className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
            <span>Provide a reliable way to contact you</span>
          </div>
        </div>
      </SidebarSection>
    </>
  );

  return (
    <IndividualEditPageLayout sidebarContent={sidebarContent}>
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-4">
          <Car className="w-8 h-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Edit Transportation</h1>
            <p className="text-gray-600">Location: {selectedLocation?.name || 'No location selected'}</p>
          </div>
        </div>
      </div>

      <form id="transportation-form" onSubmit={handleSubmit}>
        <div className="space-y-6 mb-6">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Listing Type</label>
            <select
              value={formData.listingType}
              onChange={(e) => handleChange('listingType', e.target.value as 'OFFER' | 'REQUEST')}
              className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
            >
              <option value="OFFER">Offering a Ride</option>
              <option value="REQUEST">Looking for a Ride</option>
            </select>
          </div>

          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Ride Details</h3>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => handleChange('title', e.target.value)}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors ${
                errors.title ? 'border-destructive bg-destructive/10' : 'border-input'
              }`}
              placeholder="e.g., Daily Commute to Downtown, Airport Ride"
              required
            />
            {errors.title && <p className="mt-1 text-sm text-destructive flex items-center gap-1"><AlertCircle className="w-4 h-4" /> {errors.title}</p>}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">From</label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input type="text" value={formData.routeFrom} onChange={(e) => handleChange('routeFrom', e.target.value)} className="w-full pl-10 pr-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring" placeholder="Starting point" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">To</label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input type="text" value={formData.routeTo} onChange={(e) => handleChange('routeTo', e.target.value)} className="w-full pl-10 pr-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring" placeholder="Destination" />
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Description</h3>
            <textarea
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              rows={4}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors ${
                errors.description ? 'border-destructive bg-destructive/10' : 'border-input'
              }`}
              placeholder="Describe the ride, pickup details, schedule, requirements, etc."
              required
            />
            {errors.description && <p className="text-destructive text-sm mt-1 flex items-center gap-1"><AlertCircle className="w-4 h-4" /> {errors.description}</p>}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Schedule Type</label>
              <select value={formData.scheduleType} onChange={(e) => handleChange('scheduleType', e.target.value as any)} className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring">
                {SCHEDULE_TYPES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Schedule Details</label>
              <input type="text" value={formData.schedule} onChange={(e) => handleChange('schedule', e.target.value)} className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring" placeholder="e.g., Mon-Fri 8am, Every Sunday 9am" />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Available Seats</label>
              <div className="relative">
                <Users className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input type="number" min="1" max="20" value={formData.seats} onChange={(e) => handleChange('seats', e.target.value)} className="w-full pl-10 pr-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring" placeholder="Number of seats" />
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Vehicle Type</label>
              <select value={formData.vehicleType} onChange={(e) => handleChange('vehicleType', e.target.value)} className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring">
                <option value="">Select vehicle type</option>
                {VEHICLE_TYPES.map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                <div className="flex items-center gap-2">
                  <span>Price (Optional)</span>
                  <Tooltip content="Leave empty if offering for free.">
                    <HelpCircle className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                  </Tooltip>
                </div>
              </label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input type="number" step="0.01" min="0" value={formData.price} onChange={(e) => handleChange('price', e.target.value)} className="w-full pl-10 pr-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring" placeholder="0.00" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Contact Information</label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input type="text" value={formData.contactInfo} onChange={(e) => handleChange('contactInfo', e.target.value)} className="w-full pl-10 pr-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring" placeholder="Phone number or email" />
              </div>
            </div>
          </div>
        </div>

        {errors.submit && (
          <div className="mt-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">{errors.submit}</div>
        )}
      </form>
    </IndividualEditPageLayout>
  );
}



